import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { Button, Card, Badge, Input, Select, Tabs, EmptyState } from '../components/design-system';
import { Search, Inbox, ExternalLink, Brain, XCircle, ChevronLeft, ChevronRight } from 'lucide-react';

const statusTabs = [
  { id: '', label: 'All' },
  { id: 'new', label: 'New' },
  { id: 'analyzed', label: 'Analyzed' },
  { id: 'clustered', label: 'Clustered' },
  { id: 'used', label: 'Used' },
  { id: 'discarded', label: 'Discarded' },
];

export const FeedPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => { loadItems(); }, [page, statusFilter]);

  const loadItems = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', page);
      params.set('limit', '30');
      if (statusFilter) params.set('status', statusFilter);
      if (search) params.set('search', search);
      const res = await api.getNewsItems(params.toString());
      setItems(res.items || []);
      setTotal(res.total || 0);
      setTotalPages(res.totalPages || 1);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyze = async (itemId) => {
    try {
      await api.analyzeNewsItem(itemId);
      loadItems();
    } catch (err) { console.error(err); }
  };

  const handleDiscard = async (itemId) => {
    try {
      await api.discardNewsItem(itemId);
      loadItems();
    } catch (err) { console.error(err); }
  };

  const handleSearch = (e) => { e.preventDefault(); setPage(1); loadItems(); };

  const statusBadge = (status) => {
    const variants = { new: 'info', analyzed: 'primary', clustered: 'warning', used: 'success', discarded: 'default' };
    return <Badge variant={variants[status] || 'default'}>{status}</Badge>;
  };

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Incoming Feed</h2>
          <p className="text-sm text-gray-500">{total} news items collected</p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <form onSubmit={handleSearch} className="flex-1">
          <Input icon={Search} placeholder="Search news items..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </form>
      </div>

      <Tabs
        tabs={statusTabs.map(t => ({ ...t, count: t.id === statusFilter || (!t.id && !statusFilter) ? total : undefined }))}
        activeTab={statusFilter}
        onChange={(id) => { setStatusFilter(id); setPage(1); }}
      />

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-primary-500 border-t-transparent rounded-full" />
        </div>
      ) : items.length === 0 ? (
        <EmptyState icon={Inbox} title="No news items" description="Items will appear here as sources are fetched." />
      ) : (
        <div className="space-y-2">
          {items.map((item) => (
            <Card key={item.id} className="!p-3" hover onClick={() => setSelectedItem(selectedItem?.id === item.id ? null : item)}>
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {statusBadge(item.status)}
                    <span className="text-xs text-gray-400">{item.source_name}</span>
                    <Badge variant="default">{item.source_type}</Badge>
                  </div>
                  <h3 className="text-sm font-medium text-gray-900 line-clamp-2">{item.title}</h3>
                  {item.summary && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{item.summary}</p>}
                  <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                    {item.published_at && <span>{new Date(item.published_at).toLocaleString()}</span>}
                    {item.author && <span>by {item.author}</span>}
                    {item.importance_score > 0 && (
                      <span className="text-primary-600 font-medium">{Math.round(item.importance_score * 100)}% importance</span>
                    )}
                    {item.cluster_id && <Badge variant="warning" className="text-[10px]">Cluster #{item.cluster_id}</Badge>}
                  </div>

                  {/* Expanded detail */}
                  {selectedItem?.id === item.id && (
                    <div className="mt-3 pt-3 border-t border-gray-100 space-y-2 animate-fade-in">
                      {item.content && <p className="text-xs text-gray-600 line-clamp-6">{item.content}</p>}
                      {item.topics && item.topics !== '[]' && (
                        <div className="flex flex-wrap gap-1">
                          {JSON.parse(item.topics || '[]').map((t, i) => <Badge key={i} variant="primary">{t}</Badge>)}
                        </div>
                      )}
                      {item.entities && item.entities !== '[]' && (
                        <div className="flex flex-wrap gap-1">
                          {JSON.parse(item.entities || '[]').map((e, i) => <Badge key={i} variant="info">{e}</Badge>)}
                        </div>
                      )}
                      <div className="flex items-center gap-2 pt-1">
                        {item.url && (
                          <a href={item.url} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700"
                            onClick={(e) => e.stopPropagation()}>
                            <ExternalLink className="h-3 w-3" /> View original
                          </a>
                        )}
                        {item.status === 'new' && (
                          <Button size="sm" variant="secondary" icon={Brain}
                            onClick={(e) => { e.stopPropagation(); handleAnalyze(item.id); }}>Analyze</Button>
                        )}
                        {item.status !== 'discarded' && item.status !== 'used' && (
                          <Button size="sm" variant="ghost" icon={XCircle}
                            onClick={(e) => { e.stopPropagation(); handleDiscard(item.id); }}>Discard</Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" icon={ChevronLeft} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
            <Button variant="secondary" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next<ChevronRight className="h-4 w-4 ml-1" /></Button>
          </div>
        </div>
      )}
    </div>
  );
};
